import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PageHeader, DateRange, Waiting } from "components/ui";
import { useFetch } from "hooks";
import { get, post } from "utils/api";
import { browserHistory } from "browser-history";
import useDateRange from "hooks/useDateRange";
import {
  today,
  firstDayOfThisMonth,
  firstDayOfLastMonth,
  lastDayOfLastMonth,
  firstDayOfYear
} from "utils/dates";
import moment from "moment";
import EmailQualityFilters from "./email-quality-filters";
import EmailQualityKpis from "./email-quality-kpis";
import WeeklyTrendChart from "./weekly-trend-chart";
import CategoryBreakdownChart from "./category-breakdown-chart";
import DeliverabilityHealth from "./deliverability-health";
import PatientActivityTable from "./patient-activity-table";
import "./email-quality-dashboard.scss";

const DEFAULT_PAGE_SIZE = 15;

// `lockedCompanyGuid` pins the dashboard to a single company (hides the Company filter) — used by the
// per-company Email Insights tab. `embedded` drops the page-level PageHeader so it sits cleanly inside a tab.
const EmailQualityDashboard = ({ lockedCompanyGuid = "", embedded = false }) => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    dateRangeIsValid
  } = useDateRange({
    startDate: moment().subtract(30, "days").format("MM/DD/YYYY"),
    endDate: today()
  });

  const [filters, setFilters] = useState({
    companyGuid: lockedCompanyGuid || "",
    category: "",
    teamId: "",
    coachId: ""
  });

  // Patient-activity table controls (server-side paging / sort / search).
  const [table, setTable] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: "sent",
    sortDir: "desc",
    search: ""
  });

  // Filter dropdown options (accessible companies, categories, teams, coaches).
  const {
    response: filterOptions,
    fetchData: fetchFilterOptions
  } = useFetch({
    apiFn: () => get("email-quality/filter-options"),
    defaultValue: { companies: [], categories: [], teams: [], coaches: [] }
  });

  // Composite dashboard: { Summary, WeeklyTrend[], CategoryBreakdown[] }. PascalCase — the backend
  // Web API formatter does not camel-case.
  const {
    response: dashboard,
    fetchData: fetchDashboard,
    isFetching: isFetchingDashboard,
    error: dashboardError
  } = useFetch({
    apiFn: params => post("email-quality/dashboard", params),
    defaultValue: null
  });

  // Paged patient email activity: { Rows[], TotalCount }.
  const {
    response: patients,
    fetchData: fetchPatients,
    isFetching: isFetchingPatients
  } = useFetch({
    apiFn: params => post("email-quality/patients", params),
    defaultValue: { Rows: [], TotalCount: 0 }
  });

  // Shared facet params (snake_case DTO). Team/coach are ints on the backend; send null when unset.
  const buildFilterParams = () => ({
    start_date: startDate,
    end_date: endDate,
    company_guid: lockedCompanyGuid || filters.companyGuid || null,
    category: filters.category || null,
    team_id: filters.teamId ? parseInt(filters.teamId, 10) : null,
    coach_id: filters.coachId ? parseInt(filters.coachId, 10) : null
  });

  const buildPatientParams = (overrides = {}) => {
    const t = { ...table, ...overrides };
    return {
      ...buildFilterParams(),
      search: t.search || null,
      page: t.page,
      page_size: t.pageSize,
      sort_by: t.sortBy,
      sort_dir: t.sortDir
    };
  };

  const loadDashboard = () => {
    fetchDashboard(buildFilterParams());
  };

  // Reset to page 1 whenever the facets/date change, then reload both the dashboard and the table.
  const loadAll = () => {
    const next = { ...table, page: 1 };
    setTable(next);
    fetchDashboard(buildFilterParams());
    fetchPatients(buildPatientParams({ page: 1 }));
  };

  // Load options + initial data on mount.
  useEffect(() => {
    fetchFilterOptions();
    loadDashboard();
    fetchPatients(buildPatientParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  // Table interactions reload only the table.
  const handlePageChange = page => {
    setTable(prev => ({ ...prev, page }));
    fetchPatients(buildPatientParams({ page }));
  };

  const handleSort = column => {
    setTable(prev => {
      const sortDir =
        prev.sortBy === column && prev.sortDir === "asc" ? "desc" : "asc";
      const next = { ...prev, sortBy: column, sortDir, page: 1 };
      fetchPatients(buildPatientParams(next));
      return next;
    });
  };

  const handleSearch = search => {
    setTable(prev => {
      const next = { ...prev, search, page: 1 };
      fetchPatients(buildPatientParams(next));
      return next;
    });
  };

  // Server-side CSV export → row lands on /exports (same pattern as call-quality).
  const handleExport = async () => {
    try {
      await post("email-quality/export", {
        ...buildFilterParams(),
        search: table.search || null,
        sort_by: table.sortBy,
        sort_dir: table.sortDir
      });
      browserHistory.push("/exports");
    } catch (e) {
      // errors surface on the exports page; nothing to do here
    }
  };

  const handleDatePreset = key => {
    switch (key) {
      case "last7":
        setStartDate(moment().subtract(7, "days").format("MM/DD/YYYY"));
        setEndDate(today());
        break;
      case "last30":
        setStartDate(moment().subtract(30, "days").format("MM/DD/YYYY"));
        setEndDate(today());
        break;
      case "last90":
        setStartDate(moment().subtract(90, "days").format("MM/DD/YYYY"));
        setEndDate(today());
        break;
      case "thisMonth":
        setStartDate(firstDayOfThisMonth());
        setEndDate(today());
        break;
      case "lastMonth":
        setStartDate(firstDayOfLastMonth());
        setEndDate(lastDayOfLastMonth());
        break;
      case "ytd":
        setStartDate(firstDayOfYear());
        setEndDate(today());
        break;
      default:
        break;
    }
  };

  const summary = dashboard ? dashboard.Summary : null;

  const dateRangeControls = (
    <div className="email-quality-header-controls">
      <DateRange
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleSubmit={loadAll}
        isValid={dateRangeIsValid}
        isFetching={isFetchingDashboard}
      />
    </div>
  );

  return (
    <div className="email-quality-dashboard">
      {embedded ? (
        dateRangeControls
      ) : (
        <PageHeader title="Email Quality Dashboard">{dateRangeControls}</PageHeader>
      )}

      <EmailQualityFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onApply={loadAll}
        onDatePreset={handleDatePreset}
        onExport={handleExport}
        isFetching={isFetchingDashboard}
        lockCompany={!!lockedCompanyGuid}
      />

      {isFetchingDashboard ? (
        <Waiting />
      ) : dashboardError ? (
        <div className="email-quality-error">
          Failed to load email engagement data. Please adjust your filters and
          try again.
        </div>
      ) : (
        <React.Fragment>
          <EmailQualityKpis summary={summary} />

          <div className="email-quality-charts">
            <div className="chart-card">
              <h3>Weekly Trend</h3>
              <WeeklyTrendChart data={dashboard ? dashboard.WeeklyTrend : []} />
            </div>
            <div className="chart-card">
              <h3>Opened by Category</h3>
              <CategoryBreakdownChart
                data={dashboard ? dashboard.CategoryBreakdown : []}
              />
            </div>
          </div>

          <DeliverabilityHealth summary={summary} />
        </React.Fragment>
      )}

      <PatientActivityTable
        result={patients}
        isFetching={isFetchingPatients}
        page={table.page}
        pageSize={table.pageSize}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        search={table.search}
        onPageChange={handlePageChange}
        onSort={handleSort}
        onSearch={handleSearch}
      />
    </div>
  );
};

EmailQualityDashboard.propTypes = {
  // When set, pins every query to this company GUID and hides the Company filter (per-company tab).
  lockedCompanyGuid: PropTypes.string,
  // When true, renders without the page-level PageHeader so it can sit inside a company profile tab.
  embedded: PropTypes.bool
};

export default EmailQualityDashboard;
